import React, { useState, useEffect } from 'react';
import { 
  Scissors, 
  ChevronRight,
  Clock,
  AlertCircle,
  Mail,
  Phone,
  Instagram,
  Facebook,
  Twitter 
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { authService } from './services/authService';
import { dbService } from './services/dbService';
import { notificationService } from './services/notificationService';
import { UserProfile, Service } from './types';
import { seedDatabase } from './services/seedData';
import { ADMIN_EMAILS } from './constants';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './lib/firebase';

// UI Components
import { Navbar } from './components/ui/Navbar';
import { Toast } from './components/ui/Toast';
import { ConfirmationModal } from './components/ui/ConfirmationModal';
import { ChatModal } from './components/ui/ChatModal';

// Shared Layout/Components
import { Hero } from './components/landing/Hero';
import { BarberCard } from './components/landing/BarberCard';
import { BarberDetails } from './components/landing/BarberDetails';
import { BookingFlow } from './components/landing/BookingFlow';
import { CheckoutModal } from './components/landing/CheckoutModal';

// Auth Components
import { AuthChoiceView } from './components/auth/AuthChoiceView';
import { UserRegistrationForm } from './components/auth/UserRegistrationForm';
import { BarberRegistrationForm } from './components/auth/BarberRegistrationForm';

// Dashboard Components
import { CustomerDashboard } from './components/dashboard/customer/CustomerDashboard';
import { BarberDashboard } from './components/dashboard/barber/BarberDashboard';
import { AdminDashboard } from './components/dashboard/admin/AdminDashboard';
import { ProfileSettingsModal } from './components/dashboard/barber/ProfileSettingsModal';

export default function App() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'home' | 'barber-details' | 'dashboard' | 'my-bookings' | 'login' | 'register' | 'register-barber' | 'admin-dashboard'>('home');
  const [selectedBarber, setSelectedBarber] = useState<UserProfile | null>(null);
  const [barbers, setBarbers] = useState<UserProfile[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' } | null>(null);
  const [confirmation, setConfirmation] = useState<{ 
    isOpen: boolean; 
    title: string; 
    message: string; 
    confirmText?: string;
    onConfirm: () => void; 
    isLoading: boolean 
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isLoading: false
  });
  const [isSubmittingReg, setIsSubmittingReg] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeChatBookingId, setActiveChatBookingId] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [msgUnreadMap, setMsgUnreadMap] = useState<Record<string, number>>({});
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [pendingBooking, setPendingBooking] = useState<{
    service: Service;
    date: string;
    time: string;
    price: number;
  } | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  // Sync messages for unread count and notification
  useEffect(() => {
    if (!user || user.role === 'admin' || bookings.length === 0) {
      setMsgUnreadMap({});
      return;
    }

    const activeBookings = bookings.filter(b => b.status !== 'Selesai' && b.status !== 'Batal');
    const unsubs = activeBookings.map(b => 
      dbService.onMessagesChange(b.id, user, (msgs) => {
        const unreadCount = msgs.filter(m => m.senderId !== user.uid && !m.isRead).length;
        setMsgUnreadMap(prev => {
          if (prev[b.id] === unreadCount) return prev;
          
          if (unreadCount > (prev[b.id] || 0)) {
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg && lastMsg.senderId !== user.uid) {
              notificationService.notifyNewChat(lastMsg, user);
            }
          }
          
          return { ...prev, [b.id]: unreadCount };
        });
      })
    );

    return () => unsubs.forEach(u => u());
  }, [user?.uid, bookings.length]);

  // Sync messages for active chat
  useEffect(() => {
    if (!activeChatBookingId || !user) {
        setChatMessages([]);
        return;
    }

    const unsub = dbService.onMessagesChange(activeChatBookingId, user, (msgs) => {
        setChatMessages(msgs);
        // Mark as read
        const unreadMsgs = msgs.filter(m => m.senderId !== user.uid && !m.isRead);
        if (unreadMsgs.length > 0) {
            dbService.markMessagesAsRead(activeChatBookingId, unreadMsgs.map(m => m.id));
        }
    });

    return () => unsub();
  }, [activeChatBookingId, user?.uid]);

  useEffect(() => {
    const total = Object.values(msgUnreadMap).reduce((a: number, b: number) => a + b, 0);
    setUnreadMessagesCount(total);
  }, [msgUnreadMap]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  useEffect(() => {
    if (user?.role === 'customer' && view === 'home') {
      setView('my-bookings');
    }
  }, [user?.uid]);

  useEffect(() => {
    // Sync users for admin
    if (user?.role === 'admin') {
      const unsubscribeTable = dbService.onUsersChange(setAllUsers);
      const unsubscribeRegs = dbService.onRegistrationsChange(setRegistrations);
      return () => {
        unsubscribeTable();
        unsubscribeRegs();
      };
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === 'customer' && view === 'my-bookings' && 'Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => {
        notificationService.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user?.role, view]);

  useEffect(() => {
    notificationService.setListener((title, body, type) => {
      setNotification({ message: `${title}: ${body}`, type });
    });
  }, []);

  useEffect(() => {
    let unsubProfile: (() => void) | null = null;

    const unsubAuth = authService.onAuth((firebaseUser) => {
      // Clean up previous profile listener if any
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }

      setLoading(true);
      if (firebaseUser) {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        unsubProfile = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const profile = docSnap.data() as UserProfile;
            setUser(profile);
          } else {
            setUser(null);
          }
          setLoading(false);
        }, (error) => {
          console.error("Profile sync error:", error);
          setLoading(false);
          setUser(null);
        });
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    const initData = async () => {
      await seedDatabase();
      const sData = await dbService.getServices();
      setServices(sData as any as Service[]);
    };

    const unsubBarbers = dbService.onBarbersChange((data) => {
      setBarbers(data as any as UserProfile[]);
    });

    initData();
    return () => {
      unsubAuth();
      unsubProfile?.();
      unsubBarbers();
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    let unsubBookings: () => void;
    if (user.role === 'barber') {
      unsubBookings = dbService.onBarberBookings(user.uid, (data) => {
        setBookings(prev => {
          // Detect new booking for barber
          if (prev.length > 0 && data.length > prev.length) {
            const newBooking = data[0]; // Assuming data is sorted by createdAt desc
            notificationService.notifyNewBooking(newBooking, user);
          }
          return data;
        });
      });
    } else if (user.role === 'customer') {
      unsubBookings = dbService.onCustomerBookings(user.uid, (data) => {
        setBookings(prev => {
          if (prev.length > 0) {
            data.forEach(booking => {
              const old = prev.find(b => b.id === booking.id);
              if (old && old.status !== booking.status) {
                notificationService.notifyStatusUpdate(booking, old.status, user);
              }
            });
          }
          return data;
        });
      });
    } else if (user.role === 'admin') {
      unsubBookings = dbService.onBookingsChange(setBookings);
    }

    return () => unsubBookings?.();
  }, [user]);

  const handleLogin = async (role?: UserProfile['role']) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      const profile = await authService.login(role);
      setUser(profile);
      // Redirect to the appropriate dashboard or view
      const targetView = profile.role === 'admin' ? 'admin-dashboard' : profile.role === 'barber' ? 'dashboard' : 'my-bookings';
      setView(targetView);
    } catch (e: any) {
       // Check if message says "Akun Anda belum terdaftar"
      if (e.message?.includes('belum terdaftar')) {
          setNotification({ message: 'Akun Anda belum terdaftar di sistem. Silakan mendaftar terlebih dahulu.', type: 'info' });
          setView('register');
          return;
      }

      // Ignore if user cancelled or popup closed
      if (e.code === 'auth/cancelled-popup-request' || e.code === 'auth/popup-closed-by-user' || e.message?.includes('popup-closed-by-user')) {
        return;
      }
      console.error(e);
      setNotification({ message: e.message || 'Gagal masuk', type: 'info' });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRegister = async (data: any) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      await authService.register(data);
      setNotification({ message: 'Selamat Anda telah mendaftar. Tunggu verifikasi admin untuk mengaktifkan akun Anda.', type: 'success' });
      setView('home');
    } catch (e: any) {
      if (e.code === 'auth/cancelled-popup-request' || e.code === 'auth/popup-closed-by-user' || e.message?.includes('popup-closed-by-user')) {
        return;
      }
      console.error(e);
      setNotification({ message: e.message || 'Gagal mendaftar', type: 'info' });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setView('home');
    setUser(null);
  };

  const handleBooking = async (service: Service, date: string, time: string, overridePrice?: number) => {
    if (!user) {
        setView('login');
        return;
    }
    if (!selectedBarber) return;

    setPendingBooking({
      service,
      date,
      time,
      price: overridePrice ?? service.price
    });
    setIsCheckoutOpen(true);
  };

  const handleConfirmCheckout = async (paymentMethod: 'QRIS' | 'Transfer Bank', paymentProof?: string) => {
    if (!user || !selectedBarber || !pendingBooking) return;

    try {
      await dbService.createBooking({
        customerId: user.uid,
        barberId: selectedBarber.uid,
        serviceId: pendingBooking.service.id,
        serviceName: pendingBooking.service.name,
        barberName: selectedBarber.name,
        date: pendingBooking.date,
        time: pendingBooking.time,
        status: 'Menunggu',
        totalPrice: pendingBooking.price,
        paymentMethod: paymentMethod,
        paymentProofUrl: paymentProof || '',
        paymentStatus: paymentProof ? 'Menunggu Verifikasi' : 'Belum Bayar'
      });
      setNotification({ message: 'Booking Berhasil Dikonfirmasi!', type: 'success' });
      setPendingBooking(null);
      setIsCheckoutOpen(false);
      setView('my-bookings');
    } catch (e) {
      setNotification({ message: 'Gagal melakukan booking: ' + e, type: 'info' });
    }
  };

  const handleUpdateStatus = async (bookingId: string, status: string) => {
    try {
      await dbService.updateBookingStatus(bookingId, status);
      setNotification({ message: `Status diperbarui ke ${status}`, type: 'success' });
    } catch (e) {
      setNotification({ message: 'Gagal update status: ' + e, type: 'info' });
    }
  };

  const handleUpdateBarberStatus = async (status: string) => {
    if (!user) return;
    try {
      await dbService.updateBarberStatus(user.uid, status);
      setUser({ ...user, status: status as any });
      setNotification({ message: `Status Anda kini: ${status}`, type: 'success' });
    } catch (e) {
      setNotification({ message: 'Gagal update status kapster: ' + e, type: 'info' });
    }
  };

  const handleUpdateProfile = async (data: any) => {
    if (!user) return;
    try {
      await dbService.updateUser(user.uid, data);
      setUser({ ...user, ...data });
      setNotification({ message: 'Profil berhasil diperbarui!', type: 'success' });
    } catch (error) {
      setNotification({ message: 'Gagal memperbarui profil.', type: 'info' });
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    setConfirmation({
      isOpen: true,
      title: "Batalkan Pesanan?",
      message: "Apakah Anda yakin ingin membatalkan pesanan ini? Tindakan ini tidak dapat dibatalkan.",
      isLoading: false,
      onConfirm: async () => {
        setConfirmation(prev => ({ ...prev, isLoading: true }));
        try {
          await dbService.updateBookingStatus(bookingId, 'Batal');
          setNotification({ message: 'Pesanan telah dibatalkan', type: 'info' });
          setConfirmation({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
        } catch (e) {
          setNotification({ message: 'Gagal membatalkan pesanan: ' + e, type: 'info' });
          setConfirmation(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleRegisterBarber = async (data: any) => {
    setIsSubmittingReg(true);
    try {
      const emailCheck = await dbService.checkEmailExists(data.email);
      if (emailCheck.exists) {
        throw new Error(emailCheck.message);
      }

      const targetUid = user ? user.uid : `barber-pending-${Math.random().toString(36).substring(2, 11)}`;
      await dbService.submitBarberRegistration({
        ...data,
        uid: targetUid,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      setNotification({ message: 'Pendaftaran berhasil dikirim! Silakan tunggu konfirmasi admin.', type: 'success' });
      setView('home');
    } catch (e: any) {
      setNotification({ message: 'Gagal mendaftar: ' + e.message, type: 'info' });
    } finally {
      setIsSubmittingReg(false);
    }
  };

  const handleApproveBarber = async (reg: any) => {
    try {
      if (!reg.uid.startsWith('barber-pending-')) {
        try {
          await dbService.updateUserRole(reg.uid, 'barber');
        } catch (err) {
          console.log('User profile does not exist to update role, creating a new one instead');
        }
      }
      await dbService.createUser({
        uid: reg.uid,
        name: reg.name,
        email: reg.email,
        role: 'barber',
        gender: reg.gender || 'pria',
        isVerified: true,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${reg.name}`,
        phone: reg.phone,
        experience: Number(reg.experience) || 0,
        toolsCompleteness: reg.toolsCompleteness || 'Tidak ditentukan',
        registeredViaForm: true,
        bio: reg.bio,
        specialties: reg.specialties,
        province: reg.province,
        city: reg.city,
        district: reg.district,
        status: 'Tersedia',
        rating: 5.0
      });
      await dbService.deleteRegistration(reg.id);
      setNotification({ message: `Hak akses berhasil diberikan kepada Kapster ${reg.name}!`, type: 'success' });
    } catch (e) {
      setNotification({ message: 'Gagal menyetujui: ' + e, type: 'info' });
    }
  };

  const handleSendMessage = async (bookingId: string, text: string) => {
    if (!user) return;
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;

    try {
        await dbService.sendMessage(bookingId, {
            senderId: user.uid,
            senderName: user.name,
            text,
            customerId: booking.customerId,
            barberId: booking.barberId
        });
    } catch (error) {
        setNotification({ message: 'Gagal mengirim pesan', type: 'info' });
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <Scissors className="w-12 h-12 text-emerald-500 animate-bounce" />
          <p className="text-white font-bold tracking-widest text-xs uppercase">CukurJe Loading...</p>
        </div>
      </div>
    );
  }

  const activeBookingForChat = bookings.find(b => b.id === activeChatBookingId);
  const chatPartnerName = user?.role === 'customer' ? activeBookingForChat?.barberName : activeBookingForChat?.customerName;

  return (
    <div className="min-h-screen flex flex-col">
      <AnimatePresence>
        {notification && (
          <Toast 
            message={notification.message} 
            type={notification.type} 
            onClose={() => setNotification(null)} 
          />
        )}
      </AnimatePresence>
      <ConfirmationModal 
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        isLoading={confirmation.isLoading}
        onConfirm={confirmation.onConfirm}
        onCancel={() => setConfirmation({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false })}
      />
      {!['dashboard', 'admin-dashboard', 'my-bookings'].includes(view) && (
        <Navbar 
          user={user} 
          onLogin={() => setView('login')} 
          onLogout={handleLogout} 
          onTabChange={setView as any}
          activeTab={view}
        />
      )}

      <main className="flex-1">
        <AnimatePresence mode="wait">
          {view === 'home' && (
            <motion.div
              key="home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Hero 
                onSearch={() => {}} 
                user={user}
                onDashboard={() => {
                  if (user) {
                    const targetView = user.role === 'admin' ? 'admin-dashboard' : user.role === 'barber' ? 'dashboard' : 'my-bookings';
                    setView(targetView);
                  }
                }}
              />
              <div id="kapster-unggulan" className="max-w-7xl mx-auto px-4 mt-8 relative mb-32">
                <div className="flex items-center justify-between mb-8 border-b-4 border-slate-900 pb-4">
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter italic whitespace-nowrap">Kapster <span className="text-emerald-500">Unggulan</span></h2>
                  <div className="flex gap-2">
                    <button className="p-3 bg-white hover:bg-slate-50 rounded-2xl shadow-lg border border-slate-100 transition-all active:scale-95">
                      <ChevronRight className="w-5 h-5 text-slate-900 rotate-180" />
                    </button>
                    <button className="p-3 bg-white hover:bg-slate-50 rounded-2xl shadow-lg border border-slate-100 transition-all active:scale-95">
                      <ChevronRight className="w-5 h-5 text-slate-900" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {barbers.map(barber => (
                    <BarberCard 
                      key={barber.uid} 
                      barber={barber} 
                      onNotify={(notif) => setNotification(notif)}
                      onClick={() => {
                        setSelectedBarber(barber);
                        setView('barber-details');
                      }} 
                    />
                  ))}
                </div>
                {barbers.length === 0 && (
                  <div className="bg-white/80 backdrop-blur-md rounded-[2.5rem] border-2 border-dashed border-slate-200 p-10 md:p-16 text-center max-w-4xl mx-auto flex flex-col items-center justify-center gap-6 shadow-sm">
                    <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center shadow-lg shadow-emerald-500/10">
                      <Scissors className="w-10 h-10 text-emerald-500 hover:rotate-12 transition-transform duration-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-800 mb-2">Belum Ada Kapster Terdaftar Resmi</h3>
                      <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                        Semua kapster terdaftar wajib melalui pengisian formulir pendaftaran dan proses verifikasi oleh Admin CukurJe untuk menjamin kualitas layanan terbaik.
                      </p>
                    </div>
                    <button 
                      onClick={() => setView('register-barber')}
                      className="bg-slate-900 hover:bg-emerald-600 text-white font-extrabold px-8 py-4 rounded-2xl text-xs uppercase tracking-widest transition-all shadow-xl hover:shadow-emerald-500/20 active:scale-95 cursor-pointer"
                    >
                      Daftar Jadi Kapster Sekarang
                    </button>
                  </div>
                )}
              </div>
              <BookingFlow />
            </motion.div>
          )}

          {view === 'barber-details' && selectedBarber && (
            <motion.div
              key="details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <BarberDetails 
                barber={selectedBarber} 
                services={services} 
                onBooking={handleBooking} 
              />
            </motion.div>
          )}

          {view === 'dashboard' && user?.role === 'barber' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <BarberDashboard 
                user={user} 
                bookings={bookings} 
                onUpdateStatus={handleUpdateStatus} 
                onUpdateBarberStatus={handleUpdateBarberStatus}
                unreadCount={unreadMessagesCount}
                msgUnreadMap={msgUnreadMap}
                onChat={(id: string) => setActiveChatBookingId(id)}
                onEditProfile={() => setIsProfileModalOpen(true)}
                onUpdateProfile={handleUpdateProfile}
                onLogOut={handleLogout}
                onHome={() => setView('home')}
                systemServices={services}
              />
            </motion.div>
          )}
          
          {view === 'register' && (
            <UserRegistrationForm 
              onSubmit={handleRegister}
              onSwitchToLogin={() => setView('login')}
              isLoading={isLoggingIn}
            />
          )}

          {view === 'login' && (
            <AuthChoiceView 
              onLogin={handleLogin} 
              isLoading={isLoggingIn}
            />
          )}

          {view === 'register-barber' && (
            <motion.div
              key="register-barber"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <BarberRegistrationForm 
                user={user}
                isLoading={isSubmittingReg}
                onSubmit={handleRegisterBarber}
                onCancel={() => setView('home')}
              />
            </motion.div>
          )}

          {view === 'admin-dashboard' && user?.role === 'admin' && (
            <motion.div
              key="admin-dashboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AdminDashboard 
                user={user}
                stats={{
                    totalUsers: allUsers.length,
                    totalBarbers: barbers.length,
                    totalBookings: bookings.length,
                    totalRevenue: bookings.filter(b => b.status === 'Selesai').reduce((acc, b) => acc + b.totalPrice, 0)
                }}
                users={allUsers}
                registrations={registrations}
                bookings={bookings}
                onUpdateUserRole={async (uid, role) => {
                    try {
                        await dbService.updateUserRole(uid, role as any);
                        setNotification({ message: 'Role berhasil diubah', type: 'success' });
                    } catch (error) {
                        setNotification({ message: 'Gagal mengubah role', type: 'info' });
                    }
                }}
                onApproveBarber={handleApproveBarber}
                onRejectBarber={async (id) => {
                    setConfirmation({
                        isOpen: true,
                        title: 'Tolak Pendaftaran?',
                        message: 'Data pendaftaran ini akan dihapus permanen. Lanjutkan?',
                        confirmText: 'Ya, Tolak',
                        isLoading: false,
                        onConfirm: async () => {
                            setConfirmation(prev => ({ ...prev, isLoading: true }));
                            try {
                                await dbService.deleteRegistration(id);
                                setNotification({ message: 'Pendaftaran ditolak', type: 'info' });
                                setConfirmation({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
                            } catch (error) {
                                setNotification({ message: 'Gagal menolak', type: 'info' });
                                setConfirmation(prev => ({ ...prev, isLoading: false }));
                            }
                        }
                    });
                }}
                onToggleVerify={async (uid, verified) => {
                    try {
                        await dbService.toggleUserVerification(uid, verified);
                        setNotification({ message: verified ? 'User diverifikasi' : 'Verifikasi dicabut', type: 'success' });
                    } catch (error) {
                        setNotification({ message: 'Gagal update status', type: 'info' });
                    }
                }}
                onClearUsers={async () => {
                    setConfirmation({
                        isOpen: true,
                        title: 'Erase All Users?',
                        message: 'Tindakan ini akan menghapus SELURUH pengguna non-admin. Tindakan ini permanen!',
                        confirmText: 'HAPUS SEMUA',
                        isLoading: false,
                        onConfirm: async () => {
                            setConfirmation(prev => ({ ...prev, isLoading: true }));
                            try {
                                await dbService.clearNonAdminUsers();
                                setNotification({ message: 'Data dibersihkan', type: 'info' });
                                setConfirmation({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
                            } catch (error) {
                                setNotification({ message: 'Gagal membersihkan data', type: 'info' });
                                setConfirmation(prev => ({ ...prev, isLoading: false }));
                            }
                        }
                    });
                }}
                onDeleteUser={async (uid) => {
                    setConfirmation({
                        isOpen: true,
                        title: 'Hapus User?',
                        message: 'Apakah Anda yakin ingin menghapus user ini secara permanen?',
                        confirmText: 'Ya, Hapus',
                        isLoading: false,
                        onConfirm: async () => {
                            setConfirmation(prev => ({ ...prev, isLoading: true }));
                            try {
                                await dbService.deleteUser(uid);
                                setNotification({ message: 'User dihapus', type: 'info' });
                                setConfirmation({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
                            } catch (error) {
                                setNotification({ message: 'Gagal menghapus user', type: 'info' });
                                setConfirmation(prev => ({ ...prev, isLoading: false }));
                            }
                        }
                    });
                }}
                onDeleteBooking={async (id) => {
                    setConfirmation({
                        isOpen: true,
                        title: 'Hapus Transaksi?',
                        message: 'Data transaksi ini akan dihapus dari ledger.',
                        confirmText: 'Ya, Hapus',
                        isLoading: false,
                        onConfirm: async () => {
                            setConfirmation(prev => ({ ...prev, isLoading: true }));
                            try {
                                await dbService.deleteBooking(id);
                                setNotification({ message: 'Transaksi dihapus', type: 'info' });
                                setConfirmation({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
                            } catch (error) {
                                setNotification({ message: 'Gagal menghapus transaksi', type: 'info' });
                                setConfirmation(prev => ({ ...prev, isLoading: false }));
                            }
                        }
                    });
                }}
                onLogOut={handleLogout}
                onHome={() => setView('home')}
              />
            </motion.div>
          )}

          {view === 'my-bookings' && user?.role === 'customer' && (
            <motion.div
              key="customer-dashboard"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <CustomerDashboard 
                user={user} 
                bookings={bookings} 
                onCancelBooking={handleCancelBooking} 
                onChat={(id: string) => setActiveChatBookingId(id)}
                onEditProfile={() => setIsProfileModalOpen(true)}
                unreadCount={unreadMessagesCount}
                msgUnreadMap={msgUnreadMap}
                onLogOut={handleLogout}
                onHome={() => setView('home')}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {activeChatBookingId && user && (
          <ChatModal 
            isOpen={true}
            onClose={() => setActiveChatBookingId(null)}
            bookingId={activeChatBookingId}
            user={user}
            messages={chatMessages}
            onSendMessage={handleSendMessage}
            partnerName={chatPartnerName || 'Partner'}
          />
        )}
        {isProfileModalOpen && user && (
          <ProfileSettingsModal 
            isOpen={true}
            onClose={() => setIsProfileModalOpen(false)}
            user={user}
            onUpdate={handleUpdateProfile}
          />
        )}
        {isCheckoutOpen && pendingBooking && selectedBarber && (
          <CheckoutModal
            isOpen={true}
            onClose={() => setIsCheckoutOpen(false)}
            service={pendingBooking.service}
            barber={selectedBarber}
            date={pendingBooking.date}
            time={pendingBooking.time}
            totalPrice={pendingBooking.price}
            onConfirm={handleConfirmCheckout}
          />
        )}
      </AnimatePresence>
      <footer className="bg-slate-900 text-slate-500 py-12 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="bg-emerald-500 p-2 rounded-xl">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white tracking-tight">CukurJe</span>
            </div>
            <p className="text-sm leading-relaxed max-w-xs">
              Platform revolusioner untuk mem-booking layanan cukur rambut premium. Mudah, cepat, dan terpercaya.
            </p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Navigasi</h4>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => setView('home')} className="hover:text-emerald-400">Beranda</button></li>
              <li><button className="hover:text-emerald-400">Tentang Kami</button></li>
              <li><button className="hover:text-emerald-400">Kebijakan Privasi</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Hubungi Kami</h4>
            <div className="space-y-4">
              <div className="text-sm flex flex-col gap-2 font-medium">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-emerald-500" />
                  <div className="flex flex-col gap-1">
                    {ADMIN_EMAILS.map(email => (
                      <span key={email}>{email}</span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm flex items-center gap-2 font-medium">
                <Phone className="w-4 h-4 text-emerald-500" />
                081584555655
              </p>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center">
                  <Instagram className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center">
                  <Facebook className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center">
                  <Twitter className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-12 border-t border-white/5 text-center text-xs">
          © 2026 CukurJe Marketplace. Seluruh Hak Cipta Dilindungi.
        </div>
      </footer>
    </div>
  );
}
